import type { ConfirmResult, Store } from '@wiztime/storage';
import { queries } from '@wiztime/storage';
import { RecordType } from '@wiztime/core';
import type { TFunction } from 'i18next';
import { toastUndo } from './toastStore';

/**
 * Message of a draft-confirming command (2026-10b §10.1): all confirmed, partly removed as
 * exact duplicates of confirmed records, all removed, or — for one draft — naming the project
 * of the record it duplicates. Null when nothing happened.
 */
export function confirmMessage(t: TFunction, result: ConfirmResult, projectName: string): string | null {
  const confirmed = result.confirmed.length;
  const removed = result.removedAsDuplicate.length;
  if (confirmed + removed === 0) return null;
  if (removed === 0) return t('toast.confirmedDrafts', { count: confirmed });
  if (confirmed === 0 && removed === 1) return t('toast.confirmedDraftDuplicate', { project: projectName });
  if (confirmed === 0) return t('toast.confirmedDraftsAllDuplicate', { removed });
  return t('toast.confirmedDraftsPartial', { confirmed, removed });
}

export async function projectNameOf(store: Store, projectId: string): Promise<string> {
  const project = await queries.getRecord(store.repo, projectId);
  return project?.type === RecordType.Project ? (project.data?.name ?? '') : '';
}

/** Report a confirm result with the undo toast; `projectId` names the duplicated record's project. */
export async function reportConfirm(
  store: Store,
  t: TFunction,
  result: ConfirmResult,
  projectId: string,
  undoVersion: number,
  undo: () => void,
): Promise<void> {
  const name = result.removedAsDuplicate.length > 0 ? await projectNameOf(store, projectId) : '';
  const message = confirmMessage(t, result, name);
  if (message !== null) toastUndo(message, t('toast.undo'), undoVersion, undo);
}
